import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Modal,
  Pagination,
  Table,
  Select,
  TextInput,
} from "flowbite-react";
import {
  FaArrowsLeftRight,
  FaCircleCheck,
  FaTriangleExclamation,
  FaCircleXmark,
} from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES } from "../../config/apiConfig";
import Drawer from "../Drawer";
import { FaRegEdit, FaMoneyBillWave, FaRegCopy } from "react-icons/fa";
import QueryEditForm from "../forms/QueryEditForm";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CreateQueryForm from "../forms/CreateQueryForm";
import EditQueryCustomForm from "../forms/EditQueryCustomForm";
import EmptyTable from "./EmptyTable";
import { useApplication } from "../../contexts/ApplicationContext";
import MultiSelectPopover from "../inputs/MultiSelectPopover";

/* ------------------------- helpers (inline) ------------------------- */
function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function toCsv(arr) {
  return (arr || []).filter(Boolean).join(",");
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function toMoney(n) {
  const num = Number(n);
  return Number.isFinite(num)
    ? num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";
}

const getCompanyTypeDotColor = (type) => {
  switch (type) {
    case "CLIENTE":
      return "bg-emerald-500";
    case "REPRESENTANTE":
      return "bg-purple-500";
    default:
      return "bg-gray-400";
  }
};

// Deriva a base '/admin/queries' a partir do ADMIN.GET_QUERIES
const QUERIES_BASE = (ADMIN?.GET_QUERIES || "/admin/queries")
  .split("?")[0]
  .replace(/\/$/, "");

/* ----------------------- UI Chips ------------------------ */
function Chips({ items = [], color = "gray" }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-gray-400 text-2xs">—</span>;
  }
  const colorClasses =
    color === "amber"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-gray-100 text-gray-700 border-gray-200";
  const max = 4;
  const visible = items.slice(0, max);
  const extra = Math.max(0, items.length - visible.length);
  return (
    <div className="mt-1 flex flex-wrap gap-1 items-center">
      {visible.map((label) => (
        <span
          key={label}
          className={classNames(
            "text-[9px] rounded px-1.5 border",
            colorClasses
          )}
          title={label}
        >
          {label}
        </span>
      ))}
      {extra > 0 && (
        <span
          className={classNames(
            "text-[10px] rounded px-1.5 py-0.5 border",
            colorClasses
          )}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

/* --------------------- MiniChart (barras horizontais) --------------------- */
function MiniBarList({ title, items = [], colorClass = "bg-indigo-500" }) {
  const max = Math.max(1, ...items.map((i) => i.usageCount || 0));
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 md:p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-500">Sem dados para este filtro.</div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const pct = Math.round(((it.usageCount || 0) / max) * 100);
            const label = it.name || it.title || `#${it.id}`;
            return (
              <li key={`${label}-${it.usageCount}`} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-xs text-gray-700 truncate"
                    title={label}
                  >
                    {label}
                  </span>
                  <span className="text-[11px] tabular-nums text-gray-600">
                    {it.usageCount}
                  </span>
                </div>
                <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${colorClass}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------- Componente principal ------------------------- */
const AdminQueriesTable = ({ isActive }) => {
  const queryClient = useQueryClient();
  const { user } = useApplication();
  const refetchListQueries = () => {
    queryClient.invalidateQueries(["listQueries"], {
      refetchActive: true,
      refetchInactive: true,
    });
  };

  // paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  const onPageChange = (page) => setCurrentPage(page);

  // drawers
  const [isDrawerEditQuery, setIsDrawerEditQuery] = useState(false);
  const [isDrawerEditQueryCustom, setIsDrawerEditQueryCustom] = useState(false);
  const [isDrawerCreateQuery, setIsDrawerCreateQuery] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);

  // modal de preços
  const [isPricesModalOpen, setIsPricesModalOpen] = useState(false);
  const [selectedQueryForPrices, setSelectedQueryForPrices] = useState(null);
  const [isCompaniesModalOpen, setIsCompaniesModalOpen] = useState(false);
  const [selectedQueryForCompanies, setSelectedQueryForCompanies] =
    useState(null);
  const [companiesSearch, setCompaniesSearch] = useState("");
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [queryToDuplicate, setQueryToDuplicate] = useState(null);

  // filtros
  const [filterCategory, setFilterCategory] = useState("");
  const [filterAdditional, setFilterAdditional] = useState("0");
  const [searchName, setSearchName] = useState("");
  const debouncedName = useDebouncedValue(searchName, 400);

  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedBaseQueries, setSelectedBaseQueries] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // opções dos filtros
  const { data: providersOptions = [] } = useQuery(
    ["providersOptions"],
    async () => {
      const { data } = await axios.get(ADMIN.GET_PROVIDERS_ALL);
      return data?.data?.data ?? data?.data ?? [];
    },
    { enabled: isActive }
  );

  const { data: baseQueriesOptionsRaw = [] } = useQuery(
    ["baseQueriesOptions"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_QUERIES}?itemsPerPage=-1&isModular=1`
      );
      return data?.data?.data ?? [];
    },
    { enabled: isActive }
  );

  const { data: companiesOptionsRaw = [] } = useQuery(
    ["companiesOptions"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
      );
      return data?.data?.data ?? data?.data ?? [];
    },
    { enabled: isActive }
  );

  const companiesOptions = useMemo(() => {
    const seen = new Set();
    const list =
      (companiesOptionsRaw || [])
        .filter((c) => {
          const typeId = String(c?.companyTypeId || "").toUpperCase();
          const typeName = String(c?.CompanyType?.name || "").toUpperCase();
          return typeId.includes("REPRESENT") || typeName.includes("REPRESENT");
        })
        .map((c) => {
          const name =
            c?.typeCustomer === "CPF"
              ? c?.name
              : c?.socialReason || c?.name || c?.companyName;
          return { id: c.id, name: name || `#${c.id}` };
        }) ?? [];
    return list
      .filter((x) => x.id != null)
      .filter((x) => {
        if (seen.has(x.id)) return false;
        seen.add(x.id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companiesOptionsRaw]);

  const baseQueriesOptions = useMemo(() => {
    const seen = new Set();
    const list =
      baseQueriesOptionsRaw?.map((q) => ({ id: q.id, name: q.name })) ?? [];
    return list.filter((x) => {
      if (seen.has(x.id)) return false;
      seen.add(x.id);
      return true;
    });
  }, [baseQueriesOptionsRaw]);

  // Mapa id->nome para resolver QueryCustomizedFields.queryId
  const baseQueryNameById = useMemo(() => {
    const m = new Map();
    (baseQueriesOptionsRaw || []).forEach((q) => m.set(q.id, q.name));
    return m;
  }, [baseQueriesOptionsRaw]);

  // stats visuais
  const [stats, setStats] = useState({
    topBaseQueries: [],
    topProviders: [],
  });

  // listagem principal + stats
  const { isLoading: isLoadingListQueries, data: listPayload } = useQuery(
    [
      "listQueries",
      currentPage,
      debouncedName,
      filterCategory,
      filterAdditional,
      selectedProviders,
      selectedBaseQueries,
      selectedCompanies,
    ],
    async () => {
      const qs = new URLSearchParams({
        page: String(currentPage),
        searchName: debouncedName || "",
        categoryId: filterCategory || "",
        itemsPerPage: String(itemsPerPage),
        providerIds: toCsv(selectedProviders),
        baseQueryIds: toCsv(selectedBaseQueries),
        companyIds: toCsv(selectedCompanies),
        isAdditional: filterAdditional || "",
      });
      const { data } = await axios.get(`${ADMIN.GET_QUERIES}?${qs}`);
      const payload = data?.data || {};
      setTotalPages(payload.totalPages || 1);
      setTotalCount(payload.totalCount || 0);
      if (payload.stats) {
        setStats({
          topBaseQueries: payload.stats.topBaseQueries || [],
          topProviders: payload.stats.topProviders || [],
        });
      } else {
        setStats({ topBaseQueries: [], topProviders: [] });
      }
      return payload;
    },
    { enabled: isActive }
  );

  const listQueries = listPayload?.data ?? [];

  // categorias (para os forms)
  const { data: categories } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axios.get(
        `${CATEGORIES.GET_ALL}?isModular=1&itemsPerPage=-1&includeEmpty=1`
      );
      return data?.data?.data ?? [];
    },
    { enabled: isActive }
  );

  // ====== CONTROLES DO MODAL ======
  const [companyFilter, setCompanyFilter] = useState("");
  const debouncedCompany = useDebouncedValue(companyFilter, 300);
  const [sortKey, setSortKey] = useState("marginPct"); // 'marginAbs' | 'marginPct'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  // preços por empresa
  const {
    isLoading: isLoadingPrices,
    error: errorPrices,
    data: pricesPayload,
  } = useQuery(
    [
      "companiesPrices",
      selectedQueryForPrices?.id,
      isPricesModalOpen,
      debouncedCompany,
      sortKey,
      sortDir,
    ],
    async () => {
      const qid = selectedQueryForPrices?.id;
      if (!qid) return { data: [] };

      const qs = new URLSearchParams({
        search: debouncedCompany || "",
        orderBy: sortKey,
        orderDir: sortDir,
        onlyWhenHasPivot: true,
      });

      const endpoint = `${ADMIN.GET_COMPANY_PRICES}/${qid}/companies-prices?${qs}`;
      const { data } = await axios.get(endpoint);
      return data;
    },
    { enabled: !!selectedQueryForPrices?.id && isPricesModalOpen }
  );

  const priceRows = pricesPayload?.data ?? [];
  const basePrice = Number(selectedQueryForPrices?.price ?? 0);

  // handlers
  const handleEditQuery = (query) => {
    setSelectedQuery(query);
    if (query?.queryCustomizedId) {
      setIsDrawerEditQueryCustom(true);
      setIsDrawerEditQuery(false);
    } else {
      setIsDrawerEditQuery(true);
      setIsDrawerEditQueryCustom(false);
    }
  };

  const openPricesModal = (query) => {
    setSelectedQueryForPrices(query);
    setIsPricesModalOpen(true);
    setCompanyFilter("");
    setSortKey("marginPct");
    setSortDir("desc");
  };

  const openCompaniesModal = (query) => {
    setSelectedQueryForCompanies(query);
    setIsCompaniesModalOpen(true);
    setCompaniesSearch("");
  };

  const openDuplicateModal = (query) => {
    if (!query?.id || duplicatingId) return;
    setQueryToDuplicate(query);
    setIsDuplicateModalOpen(true);
  };

  const clearAllFilters = () => {
    setFilterCategory("");
    setFilterAdditional("");
    setSearchName("");
    setSelectedProviders([]);
    setSelectedBaseQueries([]);
    setSelectedCompanies([]);
    setCurrentPage(1);
  };

  // chips de filtros ativos
  const activeChips = useMemo(() => {
    const chips = [];
    if (filterCategory) {
      const catLabel =
        filterCategory === "1"
          ? "Crédito"
          : filterCategory === "2"
          ? "Cadastral"
          : filterCategory === "3"
          ? "Veicular"
          : filterCategory;
      chips.push({
        key: "category",
        label: `Categoria: ${catLabel}`,
        onClear: () => setFilterCategory(""),
      });
    }
    if (filterAdditional !== "") {
      chips.push({
        key: "additional",
        label: `Tipo: ${filterAdditional === "1" ? "Adicional" : "Principal"}`,
        onClear: () => setFilterAdditional(""),
      });
    }
    if (searchName.trim()) {
      chips.push({
        key: "search",
        label: `Nome: ${searchName}`,
        onClear: () => setSearchName(""),
      });
    }
    if (selectedProviders.length) {
      const names = providersOptions
        .filter((p) => selectedProviders.includes(p.id))
        .map((p) => p.name)
        .slice(0, 3)
        .join(", ");
      chips.push({
        key: "providers",
        label: `Provedores: ${names}${
          selectedProviders.length > 3
            ? ` +${selectedProviders.length - 3}`
            : ""
        }`,
        onClear: () => setSelectedProviders([]),
      });
    }
    if (selectedBaseQueries.length) {
      const names = baseQueriesOptions
        .filter((p) => selectedBaseQueries.includes(p.id))
        .map((p) => p.name)
        .slice(0, 3)
        .join(", ");
      chips.push({
        key: "baseQueries",
        label: `Insumos: ${names}${
          selectedBaseQueries.length > 3
            ? ` +${selectedBaseQueries.length - 3}`
            : ""
        }`,
        onClear: () => setSelectedBaseQueries([]),
      });
    }
    if (selectedCompanies.length) {
      const names = companiesOptions
        .filter((c) => selectedCompanies.includes(c.id))
        .map((c) => c.name)
        .slice(0, 3)
        .join(", ");
      chips.push({
        key: "companies",
        label: `Empresas: ${names}${
          selectedCompanies.length > 3
            ? ` +${selectedCompanies.length - 3}`
            : ""
        }`,
        onClear: () => setSelectedCompanies([]),
      });
    }
    return chips;
  }, [
    filterCategory,
    filterAdditional,
    searchName,
    selectedProviders,
    selectedBaseQueries,
    selectedCompanies,
    providersOptions,
    baseQueriesOptions,
    companiesOptions,
  ]);

  /* -------- status styles + icon -------- */
  const getStatusMeta = (status) => {
    if (status === 1) {
      return {
        label: "Ativo",
        icon: <FaCircleCheck className="inline-block" />,
        pillClasses: "border border-green-200 bg-green-50 text-green-700",
        dotClasses: "text-green-600",
      };
    }
    if (status === 0) {
      return {
        label: "Em manutenção",
        icon: <FaTriangleExclamation className="inline-block" />,
        pillClasses: "border border-orange-200 bg-orange-50 text-orange-700",
        dotClasses: "text-orange-600",
      };
    }
    return {
      label: "Inativo",
      icon: <FaCircleXmark className="inline-block" />,
      pillClasses: "border border-red-200 bg-red-50 text-red-700",
      dotClasses: "text-red-600",
    };
  };

  // util: nomes das relacionadas a partir dos queryIds de QueryCustomizedFields
  const getRelatedBaseNames = (q) => {
    const fields = q?.QueryCustomized?.QueryCustomizedFields || [];
    const ids = Array.from(
      new Set(
        fields
          .map((f) => f?.queryId)
          .filter((x) => typeof x === "number" || typeof x === "string")
      )
    );
    const names = ids.map(
      (id) => baseQueryNameById.get(Number(id)) || `#${id}`
    );
    return names;
  };

  const normalizeFieldsForDuplicate = (qcFields) =>
    (qcFields || [])
      .map((f, idx) => ({
        priority: Number(f?.priority ?? idx + 1),
        providerId: f?.providerId ?? f?.ProviderId,
        queryId: f?.queryId ?? f?.QueryId,
        ignoreErrors: f?.ignoreErrors ?? true,
        isUniqueProvider: f?.isUniqueProvider ?? false,
      }))
      .filter((f) => f.queryId && f.providerId);

  const handleDuplicateQuery = async (q) => {
    if (!q?.id || duplicatingId) return;
    setDuplicatingId(q.id);
    try {
      const { data } = await axios.get(`${ADMIN.GET_QUERY}/${q.id}`);
      const detail = data?.data;
      if (!detail) throw new Error("Consulta não encontrada");

      const qc = detail?.QueryCustomized || {};
      const fields = normalizeFieldsForDuplicate(qc?.QueryCustomizedFields);
      const tagIds = (detail?.tags || detail?.Tags || [])
        .map((t) => t?.id)
        .filter(Boolean);
      const additionalQueryIds = detail?.isAdditional
        ? []
        : (detail?.additionalQueries || detail?.AdditionalQueries || [])
            .map((aq) => aq?.id)
            .filter(Boolean);
      const companyIds = Array.isArray(detail?.companyIds)
        ? detail.companyIds.map(Number).filter(Boolean)
        : (detail?.companies || detail?.Companies || [])
            .map((c) => c?.id || c?.companyId)
            .filter(Boolean);

      const baseName = String(detail?.name || q?.name || "Consulta");
      const payload = {
        query: {
          name: `${baseName} (Copia)`,
          price: detail?.price ?? q?.price ?? 0,
          url: "",
          categoryId: String(detail?.categoryId ?? q?.categoryId ?? ""),
          description: detail?.description ?? "",
          status: q?.status ?? detail?.status ?? 1,
          isAdditional: !!detail?.isAdditional,
          isModular: !!(detail?.isModular ?? q?.isModular),
          component: detail?.component ?? q?.component ?? null,
          providerId: detail?.providerId ?? q?.providerId ?? null,
        },
        queryCustomized: {
          parameterType: qc?.parameterType || "",
          showAlertsBoard: !!qc?.showAlertsBoard,
          hasIA: !!qc?.hasIA,
          hasTabs: !!qc?.hasTabs,
        },
        fields,
        tagIds,
        additionalQueryIds,
        companyIds,
      };

      const createdResp = await axios.post(
        `${ADMIN.GET_CUSTOMIZED_QUERY}`,
        payload
      );
      const createdQuery = createdResp?.data?.data?.query || null;
      queryClient.invalidateQueries(["listQueries"]);
      queryClient.invalidateQueries(["baseQueriesOptions"]);
      return createdQuery;
    } catch (err) {
      console.error(err);
      window.alert("Erro ao duplicar consulta.");
      return null;
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <>
      {/* Filtros */}
      <div className="rounded-lg border border-gray-200 bg-white p-3 md:p-4 mb-3 mt-2 shadow-sm">
        <div className="flex flex-col gap-3">
          {/* linha 1: busca + criar + limpar */}
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Busque por nome da consulta..."
                className="w-full rounded-md border-gray-300 text-sm bg-white"
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <Button
              className="primary"
              onClick={() => setIsDrawerCreateQuery(true)}
            >
              Criar nova consulta
            </Button>
          </div>

          {/* linha 2: categoria (filtro), tipo (adicional), multiselects */}
          <div className="flex flex-col md:flex-row gap-3 w-full items-center">
            <select
              className="block w-full md:w-48 text-sm text-gray-700 bg-gray-100 rounded-md py-2 px-2 border-0 focus:outline-none"
              value={filterCategory}
              onChange={({ target }) => {
                setFilterCategory(target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todas categorias</option>
              <option value="1">Crédito</option>
              <option value="2">Cadastral</option>
              <option value="3">Veicular</option>
            </select>

            <select
              className="block w-full md:w-48 text-sm text-gray-700 bg-gray-100 rounded-md py-2 px-2 border-0 focus:outline-none"
              value={filterAdditional}
              onChange={({ target }) => {
                setFilterAdditional(target.value);
                setCurrentPage(1);
              }}
            >
              <option value="0">Principal</option>
              <option value="1">Adicional</option>
            </select>

            <MultiSelectPopover
              label="Provedores"
              options={providersOptions}
              values={selectedProviders}
              onChange={(ids) => {
                setSelectedProviders(ids);
                setCurrentPage(1);
              }}
              emptyLabel="Buscar provedores..."
            />

            <MultiSelectPopover
              label="Insumos"
              options={baseQueriesOptions}
              values={selectedBaseQueries}
              onChange={(ids) => {
                setSelectedBaseQueries(ids);
                setCurrentPage(1);
              }}
              emptyLabel="Buscar insumos..."
            />

            <MultiSelectPopover
              label="Empresas"
              options={companiesOptions}
              values={selectedCompanies}
              onChange={(ids) => {
                setSelectedCompanies(ids);
                setCurrentPage(1);
              }}
              emptyLabel="Buscar empresas..."
            />

            <Button color="light" onClick={clearAllFilters} size="xs">
              Limpar filtros
            </Button>
          </div>

          {/* linha 3: chips de filtros ativos */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {activeChips.map((c) => (
                <span
                  key={c.key}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[11px] border border-indigo-200"
                >
                  {c.label}
                  <button
                    type="button"
                    className="ml-1 text-indigo-600 hover:text-indigo-900"
                    onClick={c.onClear}
                    aria-label={`Remover filtro ${c.key}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PAINEL VISUAL: Top Insumos / Top Provedores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <MiniBarList
          title="Insumos mais usados"
          items={stats.topBaseQueries || []}
          colorClass="bg-indigo-500"
        />
        <MiniBarList
          title="Provedores mais usados"
          items={stats.topProviders || []}
          colorClass="bg-emerald-500"
        />
      </div>

      {/* tabela */}
      {listQueries?.length > 0 ? (
        <>
          <small
            className="gap-2 items-center mt-2 mb-1 flex md:hidden justify-center"
            style={{ fontSize: 10 }}
          >
            <span>
              Deslize para o lado para visualizar todas as informações
            </span>{" "}
            <FaArrowsLeftRight />
          </small>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow mt-0 md:mt-2">
            <div className="overflow-x-auto">
              <Table hoverable striped className="text-xs xl:text-sm">
                {(listQueries?.length ?? 0) > 0 && (
                  <Table.Head className="sticky top-0 bg-gray-100">
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      ID
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-gray-100 text-gray-700">
                      Nome da consulta
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-gray-100 text-gray-700">
                      Conteúdos
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-gray-100 text-gray-700">
                      Provedores
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Ações
                    </Table.HeadCell>
                  </Table.Head>
                )}

                <Table.Body className="divide-y">
                  {!isLoadingListQueries ? (
                    (listQueries?.length ?? 0) > 0 ? (
                      listQueries.map((q, index) => {
                        const tooltipIdEdit = `tooltip-edit-query-${
                          q?.id ?? index
                        }`;
                        const tooltipIdPrices = `tooltip-prices-query-${
                          q?.id ?? index
                        }`;
                        const tooltipIdDuplicate = `tooltip-dup-query-${
                          q?.id ?? index
                        }`;

                        const status = Number(q?.status);
                        const {
                          label: statusLabel,
                          icon: statusIcon,
                          pillClasses,
                          dotClasses,
                        } = getStatusMeta(status);

                        const isAdditional = !!q?.isAdditional;

                        // nomes dos provedores (únicos)
                        const providerNames = Array.isArray(
                          q?.QueryCustomized?.QueryCustomizedFields
                        )
                          ? Array.from(
                              new Set(
                                q.QueryCustomized.QueryCustomizedFields.map(
                                  (f) => f?.Provider?.name
                                ).filter(Boolean)
                              )
                            )
                          : [];

                        const relatedNames = getRelatedBaseNames(q);
                        const companyCount = Array.isArray(q?.companies)
                          ? q.companies.length
                          : Array.isArray(q?.companyIds)
                          ? q.companyIds.length
                          : 0;
                        const isDuplicatingRow = duplicatingId === q?.id;

                        return (
                          <Table.Row
                            key={index}
                            className="bg-white focus-within:bg-indigo-50/40 hover:bg-indigo-50/40"
                          >
                            {/* ID + ícone de status */}
                            <Table.Cell className="text-center py-2 font-medium text-gray-900">
                              <div className="flex items-center justify-center gap-2">
                                <span
                                  className={classNames(
                                    "text-base",
                                    dotClasses
                                  )}
                                  title={statusLabel}
                                >
                                  {statusIcon}
                                </span>
                                <span className="tabular-nums">#{q.id}</span>
                              </div>
                            </Table.Cell>

                            {/* Nome + status (pílula clarinha) */}
                            <Table.Cell className="py-2 font-medium text-gray-900">
                              <div className="flex items-center gap-2">
                                {q?.isModular && (
                                  <span className="rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-[10px] font-semibold">
                                    Modular
                                  </span>
                                )}
                                <span
                                  className="truncate inline-block max-w-[220px] md:max-w-[320px]"
                                  title={q.name}
                                >
                                  {q.name}
                                </span>
                                <span
                                  className={classNames(
                                    "text-[9px] rounded px-1",
                                    pillClasses
                                  )}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                            </Table.Cell>

                            {/* Relacionadas (pelos queryIds dos fields) */}
                            <Table.Cell className="text-left py-2">
                              <Chips items={relatedNames} color="amber" />
                            </Table.Cell>

                            {/* Provedores */}
                            <Table.Cell className="text-left py-2">
                              {providerNames.length ? (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {providerNames.slice(0, 4).map((pname) => (
                                    <span
                                      key={pname}
                                      className="text-[10px] rounded bg-gray-100 text-gray-700 px-1.5 py-0.5"
                                    >
                                      {pname}
                                    </span>
                                  ))}
                                  {providerNames.length > 4 && (
                                    <span className="text-[10px] rounded bg-gray-100 text-gray-700 px-1.5 py-0.5">
                                      +{providerNames.length - 4}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </Table.Cell>

                            {/* Ações */}
                            <Table.Cell className="text-center py-2">
                              <div className="flex gap-3 justify-center items-center">
                                <>
                                  <Tooltip
                                    id={tooltipIdEdit}
                                    content="Editar consulta"
                                  />
                                  <FaRegEdit
                                    data-tooltip-id={tooltipIdEdit}
                                    fontSize="16"
                                    onClick={() => handleEditQuery(q)}
                                    className="cursor-pointer text-gray-500 hover:text-gray-800 transition-colors"
                                    aria-label={`Editar ${
                                      q?.name ?? "consulta"
                                    }`}
                                  />
                                </>

                                <>
                                  <Tooltip
                                    id={tooltipIdPrices}
                                    content="Ver preços por empresa"
                                  />
                                  <FaMoneyBillWave
                                    data-tooltip-id={tooltipIdPrices}
                                    fontSize="16"
                                    onClick={() => openPricesModal(q)}
                                    className="cursor-pointer text-emerald-600 hover:text-emerald-800 transition-colors"
                                    aria-label={`Preços por empresa de ${
                                      q?.name ?? "consulta"
                                    }`}
                                  />
                                </>

                                <>
                                  <Tooltip
                                    id={tooltipIdDuplicate}
                                    content="Duplicar consulta"
                                  />
                                  <FaRegCopy
                                    data-tooltip-id={tooltipIdDuplicate}
                                    fontSize="16"
                                    onClick={() =>
                                      !isDuplicatingRow && openDuplicateModal(q)
                                    }
                                    className={classNames(
                                      "transition-colors",
                                      isDuplicatingRow
                                        ? "text-gray-300 cursor-not-allowed"
                                        : "cursor-pointer text-gray-500 hover:text-gray-800"
                                    )}
                                    aria-label={`Duplicar ${
                                      q?.name ?? "consulta"
                                    }`}
                                  />
                                </>
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })
                    ) : null
                  ) : (
                    <TableLoading cols={6} />
                  )}
                </Table.Body>
              </Table>
            </div>
          </div>
        </>
      ) : (
        <EmptyTable icon="search_off">
          Nenhuma consulta cadastrada! <br /> Clique em "Criar Nova Consulta"
          para iniciar sua experiência.
        </EmptyTable>
      )}

      {/* paginação + contagem */}
      {totalCount > 0 && (
        <div className="mt-3 text-center text-sm text-gray-600">
          Exibindo {(currentPage - 1) * itemsPerPage + 1}–
          {Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount}{" "}
          registros
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      {/* drawers */}
      <Drawer
        title={`Editar consulta`}
        onClose={setIsDrawerEditQuery}
        isOpen={isDrawerEditQuery}
        className={"w-full md:w-4/6 lg:w-2/6"}
      >
        {isDrawerEditQuery && selectedQuery && (
          <QueryEditForm
            key={selectedQuery?.id || "query-edit"}
            query={selectedQuery}
            categories={categories}
            onClose={(v) => {
              setIsDrawerEditQuery(v);
              if (!v) setSelectedQuery(null);
            }}
            fetchQueries={() => {
              refetchListQueries();
            }}
          />
        )}
      </Drawer>

      {isDrawerEditQueryCustom && selectedQuery && (
        <EditQueryCustomForm
          key={selectedQuery?.id || "query-custom-edit"}
          categories={categories}
          onClose={(v) => {
            setIsDrawerEditQueryCustom(v);
            if (!v) setSelectedQuery(null);
          }}
          fetchQueries={() => {
            refetchListQueries();
          }}
          query={selectedQuery}
          isOpen={isDrawerEditQueryCustom}
        />
      )}

      <CreateQueryForm
        categories={categories}
        onClose={setIsDrawerCreateQuery}
        fetchQueries={() => {
          refetchListQueries();
        }}
        isOpen={isDrawerCreateQuery}
      />

      {/* Modal de Preços */}
      <Modal
        show={isPricesModalOpen}
        size="5xl"
        onClose={() => setIsPricesModalOpen(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-3">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Preços por empresa —{" "}
                  <span className="text-indigo-700">
                    {selectedQueryForPrices?.name ?? "Consulta"}
                  </span>
                </h3>
                <p className="text-sm text-gray-600">
                  Preço base desta consulta:{" "}
                  <strong>{toMoney(basePrice)}</strong>
                </p>
              </div>

              {/* Filtros e ordenação dentro do modal */}
              <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <TextInput
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  placeholder="Filtrar por empresa..."
                />
                <div className="flex gap-2">
                  <Select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                  >
                    <option value="marginPct">Ordenar por Margem (%)</option>
                    <option value="marginAbs">Ordenar por Margem (R$)</option>
                  </Select>
                  <Select
                    value={sortDir}
                    onChange={(e) => setSortDir(e.target.value)}
                  >
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                  </Select>
                </div>
              </div>
            </div>

            {/* Legenda */}
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span className="font-medium text-gray-700">Legenda:</span>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />{" "}
                Cliente
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-purple-500 inline-block" />{" "}
                Representante
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
              <div className="overflow-x-auto">
                <Table hoverable striped className="text-xs xl:text-sm">
                  <Table.Head className="sticky top-0 bg-gray-100">
                    <Table.HeadCell className="bg-gray-100 text-gray-700">
                      Empresa
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Custo
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Preço aplicado
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Margem (R$)
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Margem (%)
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y">
                    {isLoadingPrices ? (
                      <TableLoading cols={5} />
                    ) : errorPrices ? (
                      <tr>
                        <td
                          className="py-6 text-center text-red-600"
                          colSpan={5}
                        >
                          Erro ao carregar preços.
                        </td>
                      </tr>
                    ) : priceRows?.length ? (
                      priceRows.map((r) => {
                        const defaultPrice = Number(
                          r?.defaultQueryPrice ?? basePrice ?? 0
                        );
                        const applied = Number(
                          r?.applicablePrice ?? defaultPrice
                        );
                        const marginAbs = applied - defaultPrice;
                        const marginPct =
                          defaultPrice > 0
                            ? (marginAbs / defaultPrice) * 100
                            : null;

                        const isBadMargin =
                          marginPct !== null && marginPct < 10;

                        const dotColor = getCompanyTypeDotColor(
                          r?.companyTypeId
                        );

                        return (
                          <Table.Row
                            key={`${r.companyId}-${r.queryId}`}
                            className="bg-white hover:bg-indigo-50/40"
                          >
                            <Table.Cell className="py-2 font-medium text-gray-900">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-2.5 h-2.5 rounded-full ${dotColor}`}
                                />
                                <div
                                  className="truncate max-w-[320px]"
                                  title={r.companyName}
                                >
                                  {r.companyName || `#${r.companyId}`}
                                </div>
                              </div>
                            </Table.Cell>
                            <Table.Cell className="text-center py-2">
                              {toMoney(defaultPrice)}
                            </Table.Cell>
                            <Table.Cell className="text-center py-2">
                              {toMoney(applied)}
                            </Table.Cell>
                            <Table.Cell className="text-center py-2">
                              <span
                                className={classNames(
                                  "tabular-nums",
                                  isBadMargin
                                    ? "text-red-700"
                                    : "text-emerald-700"
                                )}
                              >
                                {toMoney(marginAbs)}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="text-center py-2">
                              <span
                                className={classNames(
                                  "tabular-nums",
                                  marginPct === null
                                    ? "text-gray-500"
                                    : isBadMargin
                                    ? "text-red-700"
                                    : "text-emerald-700"
                                )}
                              >
                                {marginPct === null
                                  ? "—"
                                  : `${marginPct.toFixed(1)}%`}
                              </span>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          className="py-6 text-center text-gray-500"
                          colSpan={5}
                        >
                          Nenhuma empresa encontrada para esta consulta.
                        </td>
                      </tr>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </div>

            <div className="flex justify-end">
              <Button color="gray" onClick={() => setIsPricesModalOpen(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal de Empresas */}
      <Modal
        show={isCompaniesModalOpen}
        size="3xl"
        onClose={() => setIsCompaniesModalOpen(false)}
        popup
      >
        <Modal.Header className="flex items-center gap-2 text-base px-5">
          <div className="text-base">
            {selectedQueryForCompanies?.name ?? "Consulta"}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            {(() => {
              const all = Array.isArray(selectedQueryForCompanies?.companies)
                ? selectedQueryForCompanies.companies
                : [];
              const representatives = all.filter(
                (c) => c?.type === "REPRESENTANTE"
              );

              const normalized = representatives
                .map((c) => ({
                  ...c,
                  name: c?.name || `#${c?.id}`,
                  type:
                    c?.type === "REPRESENTANTE" ? "REPRESENTANTE" : "CLIENTE",
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

              const term = companiesSearch.trim().toLowerCase();
              const filtered = normalized.filter((c) => {
                return term ? c.name.toLowerCase().includes(term) : true;
              });

              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                      <div className="text-[11px] text-purple-700">
                        Representantes vinculados
                      </div>
                      <div className="text-lg font-semibold text-purple-800">
                        {representatives.length}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex-1">
                      <TextInput
                        value={companiesSearch}
                        onChange={(e) => setCompaniesSearch(e.target.value)}
                        placeholder="Buscar por nome..."
                      />
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
                    <div className="max-h-80 overflow-auto divide-y">
                      {filtered.length > 0 ? (
                        filtered.map((c) => {
                          const dotColor = getCompanyTypeDotColor(c.type);
                          const typeLabel =
                            c.type === "REPRESENTANTE"
                              ? "Representante"
                              : "Cliente";
                          return (
                            <div
                              key={`${selectedQueryForCompanies?.id}-${c.id}`}
                              className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-indigo-50/40"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  className={`w-2.5 h-2.5 rounded-full ${dotColor}`}
                                />
                                <span className="truncate text-sm text-gray-800">
                                  {c.name}
                                </span>
                              </div>
                              <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 text-[10px] font-medium">
                                {typeLabel}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="py-8 text-center text-sm text-gray-500">
                          Nenhum representante encontrado.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="flex justify-end">
              <Button
                color="gray"
                onClick={() => setIsCompaniesModalOpen(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Modal de confirmação de duplicação */}
      <Modal
        show={isDuplicateModalOpen}
        size="md"
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setQueryToDuplicate(null);
        }}
      >
        <Modal.Header
          className="
        py-2"
        >
          <span className="text-base mt-1">Duplicar consulta</span>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="text-sm text-gray-700 space-y-2">
            <p>
              Deseja realmente duplicar a consulta{" "}
              <span className="font-semibold">
                {queryToDuplicate?.name || "selecionada"}
              </span>
              ?
            </p>
            <p className="text-xs text-gray-500">
              Será criada uma nova consulta com os mesmos campos, tags,
              adicionais e empresas vinculadas.
            </p>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              onClick={() => {
                setIsDuplicateModalOpen(false);
                setQueryToDuplicate(null);
              }}
              disabled={!!duplicatingId}
              className="bg-gray-500"
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                setIsDuplicateModalOpen(false);
                const created = queryToDuplicate
                  ? await handleDuplicateQuery(queryToDuplicate)
                  : null;
                setQueryToDuplicate(null);
                if (created?.id) {
                  setSelectedQuery(created);
                  setIsDrawerEditQueryCustom(true);
                  setIsDrawerEditQuery(false);
                }
              }}
              isProcessing={!!duplicatingId}
              disabled={!queryToDuplicate || !!duplicatingId}
            >
              Duplicar
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminQueriesTable;
